import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import Errors from '../../../shared/validation/errors';
import headersBuilder, { Method, buildPublicGetRequest } from '../../../utils/request';
import { CurrencyInfoRequest } from '../../../types/settingsTypes';

export const FETCH_SETTINGS_CURRENCIES = 'FETCH_SETTINGS_CURRENCIES';
export const SET_CONFIG_CURRENCY_SYMBOLS = 'SET_CONFIG_CURRENCY_SYMBOLS';

export type FetchSettingsCurrenciesAction = {
  type: string;
  settings: CurrencyInfoRequest[];
};

export type SetConfigCurrencySymbolsAction = {
  type: string;
  currency: Record<string, string>;
};

const decodeHtmlEntity = (value: string): string => {
  if (!value) {
    return '';
  }

  const named: Record<string, string> = {
    '&euro;': '€',
    '&pound;': '£',
    '&yen;': '¥',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&apos;': "'"
  };

  if (named[value]) {
    return named[value];
  }

  const numericEntity = value.match(/^&#([0-9]+);$/);
  if (numericEntity) {
    const code = Number(numericEntity[1]);
    try {
      return String.fromCodePoint(code);
    } catch {
      return value;
    }
  }

  const hexEntity = value.match(/^&#x([0-9a-fA-F]+);$/);
  if (hexEntity) {
    const code = Number.parseInt(hexEntity[1], 16);
    try {
      return String.fromCodePoint(code);
    } catch {
      return value;
    }
  }

  return value;
};

const mapToCurrencySymbolsConfig = (settings: CurrencyInfoRequest[]): Record<string, string> => {
  return settings.reduce<Record<string, string>>((accumulator, currency) => {
    if (!currency.Name) {
      return accumulator;
    }

    return {
      ...accumulator,
      [currency.Name]: decodeHtmlEntity(currency.Symbol)
    };
  }, {});
};

const normalizeCurrenciesSettings = (settings: any): CurrencyInfoRequest[] => {
  const list = Array.isArray(settings) ? settings : settings?.Currencies || settings?.Currency || [];

  return list.map((currency: any) => {
    return {
      ...new CurrencyInfoRequest(),
      ...currency,
      Name: String(currency?.Name || '').toUpperCase(),
      Symbol: String(currency?.Symbol || ''),
      Digits: Number(currency?.Digits ?? 0)
    };
  });
};

const fetchSettingsCurrencies = (settings: CurrencyInfoRequest[]): FetchSettingsCurrenciesAction => {
  return {
    type: FETCH_SETTINGS_CURRENCIES,
    settings
  };
};

const setConfigCurrencySymbols = (currency: Record<string, string>): SetConfigCurrencySymbolsAction => {
  return {
    type: SET_CONFIG_CURRENCY_SYMBOLS,
    currency
  };
};

const fetchCurrenciesSettingsFromApi = (api: string): Promise<Response> => {
  return fetch(`${api}/settings/currency`, buildPublicGetRequest());
};

const postCurrenciesSettingsToApi = (api: string, request: CurrencyInfoRequest[]): Promise<Response> => {
  return fetch(`${api}/settings/currency`, headersBuilder(Method.POST, request));
};

export const fetchCurrenciesSettingsAsync = (api: string) => {
  return (dispatch: Dispatch): void => {
    if (!api) {
      return;
    }

    fetchCurrenciesSettingsFromApi(api)
      .then((response: Response) => {
        if (!response.ok) {
          return null;
        }
        return response.json();
      })
      .then((settings) => {
        if (!settings) {
          return;
        }

        const normalizedSettings = normalizeCurrenciesSettings(settings);
        dispatch(fetchSettingsCurrencies(normalizedSettings));
        dispatch(setConfigCurrencySymbols(mapToCurrencySymbolsConfig(normalizedSettings)));
      })
      .catch(() => {
        // Ignore errors: keep current frontend values.
      });
  };
};

export const editCurrenciesSettingsAsync = (api: string, request: CurrencyInfoRequest[]) => {
  return (dispatch: Dispatch): void => {
    postCurrenciesSettingsToApi(api, request)
      .then((response: Response) => Errors.checkResponseForErrors(response, api))
      .then((settings: any) => {
        const normalizedSettings = normalizeCurrenciesSettings(settings);
        dispatch(fetchSettingsCurrencies(normalizedSettings));
        dispatch(setConfigCurrencySymbols(mapToCurrencySymbolsConfig(normalizedSettings)));
        toast.success('Currencies settings edited successfully!', {
          position: toast.POSITION.BOTTOM_RIGHT
        });
      })
      .catch((error: Error) => Errors.throwErrorMessage(error));
  };
};
